// Generate a Tally-compatible XML for direct bills.
// The XML follows Tally's `<ENVELOPE><HEADER><BODY><IMPORTDATA>` schema for Sales vouchers.
// Import this file in Tally: Gateway → Import → Vouchers → pick this file → Yes.
//
// Notes:
//   * `date` is stored as YYYYMMDD (Tally requirement).
//   * Party ledger, Sales Account, and tax ledgers must already exist in the target Tally
//     company. Defaults below match a typical single-branch pharmacy chart of accounts.

const CDATA = (s) => `<![CDATA[${(s ?? "").toString().replace(/\]\]>/g, "]]]]><![CDATA[>")}]]>`;
const ymd = (isoOrDate) => {
  const d = new Date(isoOrDate);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`;
};
const num = (n) => Number(n || 0).toFixed(2);

/**
 * Build a Tally XML for a list of bills.
 * @param {Array} bills — the direct_bills documents.
 * @param {Object} [opts]
 * @param {string} [opts.company]    Tally company name (SVCURRENTCOMPANY).
 * @param {string} [opts.salesLedger]
 * @param {string} [opts.cgstLedger]
 * @param {string} [opts.sgstLedger]
 * @param {string} [opts.roundoffLedger]
 * @returns {string} XML text
 */
export function buildTallyXml(bills, opts = {}) {
  const company = opts.company || "Badarinath Ayurveda Hospital";
  const salesLedger = opts.salesLedger || "Sales - Pharmacy";
  const cgstLedger = opts.cgstLedger || "CGST Output";
  const sgstLedger = opts.sgstLedger || "SGST Output";
  const roundoffLedger = opts.roundoffLedger || "Rounded Off";

  const vouchers = bills.map((b) => {
    const dateYmd = ymd(b.generated_at || new Date());
    const items = b.items || [];
    // Group per GST rate → one inventory allocation per rate keeps tax rows minimal.
    const byRate = {};
    items.forEach((it) => {
      const rate = Number(it.gst_pct || 0);
      if (!byRate[rate]) byRate[rate] = { taxable: 0, cgst: 0, sgst: 0 };
      byRate[rate].taxable += Number(it.taxable ?? (Number(it.qty) * Number(it.price)));
      byRate[rate].cgst += Number(it.cgst || 0);
      byRate[rate].sgst += Number(it.sgst || 0);
    });

    // Party (customer) leg — negative for a sales voucher (party ledger is deemed-positive → amount is stored as negative of total for a "receipt into sales" entry).
    const partyLine = `
      <ALLLEDGERENTRIES.LIST>
        <LEDGERNAME>${CDATA(b.customer_name || "Cash")}</LEDGERNAME>
        <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
        <AMOUNT>-${num(b.total)}</AMOUNT>
      </ALLLEDGERENTRIES.LIST>`;

    // Sales ledger leg (positive) — split per GST rate group.
    const salesLines = Object.entries(byRate).map(([rate, v]) => `
      <ALLLEDGERENTRIES.LIST>
        <LEDGERNAME>${CDATA(rate === "0" ? salesLedger : `${salesLedger} @ ${rate}%`)}</LEDGERNAME>
        <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
        <AMOUNT>${num(v.taxable)}</AMOUNT>
      </ALLLEDGERENTRIES.LIST>`).join("");

    const cgstLine = Number(b.cgst_total || 0) > 0 ? `
      <ALLLEDGERENTRIES.LIST>
        <LEDGERNAME>${CDATA(cgstLedger)}</LEDGERNAME>
        <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
        <AMOUNT>${num(b.cgst_total)}</AMOUNT>
      </ALLLEDGERENTRIES.LIST>` : "";

    const sgstLine = Number(b.sgst_total || 0) > 0 ? `
      <ALLLEDGERENTRIES.LIST>
        <LEDGERNAME>${CDATA(sgstLedger)}</LEDGERNAME>
        <ISDEEMEDPOSITIVE>No</ISDEEMEDPOSITIVE>
        <AMOUNT>${num(b.sgst_total)}</AMOUNT>
      </ALLLEDGERENTRIES.LIST>` : "";

    const discountLine = Number(b.discount || 0) > 0 ? `
      <ALLLEDGERENTRIES.LIST>
        <LEDGERNAME>${CDATA("Discount Allowed")}</LEDGERNAME>
        <ISDEEMEDPOSITIVE>Yes</ISDEEMEDPOSITIVE>
        <AMOUNT>-${num(b.discount)}</AMOUNT>
      </ALLLEDGERENTRIES.LIST>` : "";

    return `
    <TALLYMESSAGE xmlns:UDF="TallyUDF">
      <VOUCHER VCHTYPE="Sales" ACTION="Create" OBJVIEW="Invoice Voucher View">
        <DATE>${dateYmd}</DATE>
        <NARRATION>${CDATA(`Direct bill ${b.bill_no} · Branch ${b.branch_code}${b.mrn ? ` · UHID ${b.mrn}` : ""}`)}</NARRATION>
        <VOUCHERTYPENAME>Sales</VOUCHERTYPENAME>
        <VOUCHERNUMBER>${CDATA(b.bill_no)}</VOUCHERNUMBER>
        <PARTYLEDGERNAME>${CDATA(b.customer_name || "Cash")}</PARTYLEDGERNAME>
        <PARTYNAME>${CDATA(b.customer_name || "Cash")}</PARTYNAME>
        <BASICBUYERNAME>${CDATA(b.customer_name || "Cash")}</BASICBUYERNAME>
        <PARTYGSTIN>${CDATA(b.customer_gstin || "")}</PARTYGSTIN>
        <REFERENCE>${CDATA(b.bill_no)}</REFERENCE>
        <VOUCHERKEY>${CDATA(b.id || b.bill_no)}</VOUCHERKEY>
        <PLACEOFSUPPLY>${CDATA("Kerala")}</PLACEOFSUPPLY>
        ${partyLine}
        ${salesLines}
        ${cgstLine}
        ${sgstLine}
        ${discountLine}
      </VOUCHER>
    </TALLYMESSAGE>`;
  }).join("");

  return `<?xml version="1.0" encoding="UTF-8"?>
<ENVELOPE>
  <HEADER>
    <TALLYREQUEST>Import Data</TALLYREQUEST>
  </HEADER>
  <BODY>
    <IMPORTDATA>
      <REQUESTDESC>
        <REPORTNAME>Vouchers</REPORTNAME>
        <STATICVARIABLES>
          <SVCURRENTCOMPANY>${CDATA(company)}</SVCURRENTCOMPANY>
        </STATICVARIABLES>
      </REQUESTDESC>
      <REQUESTDATA>${vouchers}
      </REQUESTDATA>
    </IMPORTDATA>
  </BODY>
</ENVELOPE>`;
}

export function downloadTallyXml(bills, filename = "tally-vouchers.xml", opts = {}) {
  const xml = buildTallyXml(bills, opts);
  const blob = new Blob([xml], { type: "application/xml" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
